import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ConfigRoutes, getPath } from "../../routes/routes.path";
import { ButtonLarge, ButtonLink, PopUp } from "../../shared/uikit";

/*
 * TODO: Replace mock data with real API data once the license upgrade endpoint is available.
 * Requires POST /api/license/upgrade endpoint to process upgrade requests.
 * Currently using mock data for UI development and testing.
 */

interface PlanFeature {
  label: string;
  individual: string;
  business: string;
  enterprise: string;
}

interface Plan {
  key: string;
  name: string;
  icon: string;
  price: string;
  description: string;
  isCurrent: boolean;
  badge?: string;
}

const PLANS: Plan[] = [
  {
    key: "individual",
    name: "Individual",
    icon: "👤",
    price: "Free",
    description: "For personal use and small teams",
    isCurrent: true,
    badge: "Current Plan",
  },
  {
    key: "business",
    name: "Business",
    icon: "🏢",
    price: "Contact Sales",
    description: "Requires company registration, more features",
    isCurrent: false,
  },
  {
    key: "enterprise",
    name: "Enterprise",
    icon: "🏛️",
    price: "Contact Sales",
    description: "Full features, no company needed",
    isCurrent: false,
  },
];

const PLAN_FEATURES: PlanFeature[] = [
  { label: "Users", individual: "5", business: "50", enterprise: "Unlimited" },
  {
    label: "Groups",
    individual: "10",
    business: "100",
    enterprise: "Unlimited",
  },
  {
    label: "Devices",
    individual: "8",
    business: "50",
    enterprise: "Unlimited",
  },
  {
    label: "Company Registration",
    individual: "❌",
    business: "✅",
    enterprise: "✅",
  },
  {
    label: "Encryption Keys",
    individual: "Basic",
    business: "Advanced",
    enterprise: "Full",
  },
  { label: "API Access", individual: "❌", business: "✅", enterprise: "✅" },
  {
    label: "Priority Support",
    individual: "❌",
    business: "✅",
    enterprise: "24/7 Support",
  },
  {
    label: "Audit Logs",
    individual: "❌",
    business: "Basic",
    enterprise: "Advanced",
  },
];

export const LicenseUpgradePage = () => {
  const navigate = useNavigate();
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  const handleRequestUpgrade = () => {
    setShowUpgradeModal(true);
  };

  const handleCloseModal = () => {
    setShowUpgradeModal(false);
  };

  return (
    <div style={{ padding: "1rem" }}>
      <h2>⬆ Upgrade License</h2>
      <p style={{ color: "var(--second-color)" }}>
        Compare plans and upgrade your license for additional features.
      </p>

      {/* Current Plan Summary */}
      <div
        style={{
          marginTop: "1rem",
          padding: "1rem 1.25rem",
          border: "1px solid var(--border-color)",
          borderRadius: "12px",
          background: "var(--second-background)",
        }}
      >
        <h3 style={{ margin: "0 0 0.5rem" }}>📋 Current Plan Summary</h3>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.75rem",
            marginTop: "0.5rem",
          }}
        >
          <span style={{ fontSize: "28px" }}>👤</span>
          <div>
            <strong style={{ fontSize: "16px" }}>Individual Plan (Free)</strong>
            <p
              style={{
                color: "var(--second-color)",
                fontSize: "13px",
                margin: "0.25rem 0 0",
              }}
            >
              For personal use and small teams
            </p>
          </div>
          <span
            style={{
              display: "inline-block",
              padding: "3px 10px",
              borderRadius: "8px",
              fontSize: "12px",
              fontWeight: 600,
              background: "#28a745",
              color: "var(--first-color-inv)",
              marginLeft: "auto",
            }}
          >
            🟢 Active
          </span>
        </div>
      </div>

      {/* Available Plans Comparison */}
      <h3 style={{ marginTop: "1.5rem" }}>📊 Available Plans</h3>

      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        {PLANS.map((plan) => (
          <div
            key={plan.key}
            style={{
              padding: "1rem 1.25rem",
              border: plan.isCurrent
                ? "2px solid var(--accent-color)"
                : "1px solid var(--color-border)",
              borderRadius: "var(--radius-md)",
              background: "var(--first-background)",
            }}
          >
            <div
              style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}
            >
              <span style={{ fontSize: "28px" }}>{plan.icon}</span>
              <div style={{ flex: 1 }}>
                <strong style={{ fontSize: "16px", display: "block" }}>
                  {plan.name}
                  {plan.badge && (
                    <span
                      style={{
                        display: "inline-block",
                        padding: "2px 8px",
                        borderRadius: "var(--radius-sm)",
                        fontSize: "11px",
                        fontWeight: 600,
                        background: "var(--color-bg-secondary)",
                        color: "var(--second-color)",
                        marginLeft: "0.5rem",
                      }}
                    >
                      {plan.badge}
                    </span>
                  )}
                </strong>
                <span
                  style={{ color: "var(--second-color)", fontSize: "13px" }}
                >
                  {plan.price}
                </span>
                <p
                  style={{
                    color: "var(--second-color)",
                    fontSize: "12px",
                    margin: "0.25rem 0 0",
                  }}
                >
                  {plan.description}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Feature Comparison Table */}
      <h3 style={{ marginTop: "1.5rem" }}>📋 Feature Comparison</h3>

      <div
        style={{
          marginTop: "0.5rem",
          overflowX: "auto",
          border: "1px solid var(--color-border)",
          borderRadius: "var(--radius-md)",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "14px",
          }}
        >
          <thead>
            <tr style={{ background: "var(--color-bg-secondary)" }}>
              <th
                style={{
                  padding: "0.75rem 0.5rem",
                  textAlign: "left",
                  borderBottom: "1px solid var(--color-border)",
                }}
              >
                Feature
              </th>
              <th
                style={{
                  padding: "0.75rem 0.5rem",
                  textAlign: "center",
                  borderBottom: "1px solid var(--color-border)",
                }}
              >
                👤 Individual
              </th>
              <th
                style={{
                  padding: "0.75rem 0.5rem",
                  textAlign: "center",
                  borderBottom: "1px solid var(--color-border)",
                }}
              >
                🏢 Business
              </th>
              <th
                style={{
                  padding: "0.75rem 0.5rem",
                  textAlign: "center",
                  borderBottom: "1px solid var(--color-border)",
                }}
              >
                🏛️ Enterprise
              </th>
            </tr>
          </thead>
          <tbody>
            {PLAN_FEATURES.map((feature, index) => (
              <tr
                key={feature.label}
                style={{
                  background:
                    index % 2 === 0
                      ? "transparent"
                      : "var(--color-bg-secondary)",
                }}
              >
                <td
                  style={{
                    padding: "0.6rem 0.5rem",
                    fontWeight: 600,
                    borderBottom: "1px solid var(--color-border)",
                    color: "var(--first-color)",
                  }}
                >
                  {feature.label}
                </td>
                <td
                  style={{
                    padding: "0.6rem 0.5rem",
                    textAlign: "center",
                    borderBottom: "1px solid var(--color-border)",
                    color: "var(--second-color)",
                  }}
                >
                  {feature.individual}
                </td>
                <td
                  style={{
                    padding: "0.6rem 0.5rem",
                    textAlign: "center",
                    borderBottom: "1px solid var(--color-border)",
                    color: "var(--color-text)",
                  }}
                >
                  {feature.business}
                </td>
                <td
                  style={{
                    padding: "0.6rem 0.5rem",
                    textAlign: "center",
                    borderBottom: "1px solid var(--color-border)",
                    color: "var(--color-text)",
                  }}
                >
                  {feature.enterprise}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Request Upgrade Button */}
      <ButtonLarge
        title="⬆ Request Upgrade"
        onClick={handleRequestUpgrade}
        mode="black"
      />

      {/* Navigation */}
      <ButtonLink
        title="← Back to License Info"
        onClick={() => navigate(getPath(ConfigRoutes.LICENSE))}
      />

      {/* TODO note */}
      <div
        style={{
          marginTop: "1rem",
          padding: "0.75rem 1rem",
          border: "1px dashed var(--color-border)",
          borderRadius: "var(--radius-md)",
          background: "var(--color-bg)",
          fontSize: "12px",
          color: "var(--second-color)",
        }}
      >
        <p style={{ margin: 0 }}>
          📝 <strong>TODO:</strong> Replace mock data with real API data once
          <code
            style={{
              fontSize: "12px",
              background: "var(--color-bg-secondary)",
              padding: "1px 4px",
              borderRadius: "3px",
            }}
          >
            POST /api/license/upgrade
          </code>
          endpoint is available.
        </p>
      </div>

      {/* Upgrade Modal */}
      {showUpgradeModal && (
        <PopUp
          content={
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: "48px", marginBottom: "1rem" }}>📋</div>
              <h3 style={{ margin: "0 0 0.5rem" }}>Upgrade Request</h3>
              <p
                style={{
                  color: "var(--second-color)",
                  fontSize: "14px",
                  lineHeight: "1.5",
                }}
              >
                Upgrade requests are not yet available. Please contact sales.
              </p>
              <ButtonLarge
                title="Got it"
                onClick={handleCloseModal}
                mode="black"
              />
            </div>
          }
          isCloseButton={true}
          onClick={handleCloseModal}
        />
      )}
    </div>
  );
};
